/*
 * LICENSE PROTECTED SOFTWARE
 * (c) 2026  @cemdemirci0101
 */

import React, { useState, useEffect, useCallback } from 'react';
import { statisticsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  BarChart3, Users, MapPin, UsersRound, Lightbulb, Vote,
  Eye, ClipboardList, Printer, FileDown, TrendingUp, Activity,
  Calendar, Megaphone, Circle, RefreshCw,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid,
} from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const ALLOWED_ROLES = ['admin', 'kent_konseyi_baskani', 'genel_sekreter', 'genel_sekreter_yardimcisi'];
const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899', '#84CC16', '#F97316', '#6366F1'];

const ROLE_LABELS = {
  admin: 'Sistem Yoneticisi',
  kent_konseyi_baskani: 'Kent Konseyi Baskani',
  genel_sekreter: 'Genel Sekreter',
  genel_sekreter_yardimcisi: 'Genel Sekreter Yard.',
  meclis_baskani: 'Meclis Baskani',
  cg_baskani: 'CG Baskani',
  yk_uyesi: 'YK Uyesi',
  vatandas: 'Vatandas',
  muhtar: 'Muhtar',
  stk: 'STK',
};

const Kpi = ({ icon: Icon, label, value, sub, color = 'blue' }) => {
  const palette = {
    blue: 'bg-blue-50 text-blue-700 ring-blue-100',
    green: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    amber: 'bg-amber-50 text-amber-700 ring-amber-100',
    purple: 'bg-purple-50 text-purple-700 ring-purple-100',
    red: 'bg-red-50 text-red-700 ring-red-100',
    slate: 'bg-slate-50 text-slate-700 ring-slate-200',
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-sm transition-all" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-9 h-9 rounded-xl ring-1 flex items-center justify-center ${palette[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        {sub && <span className="text-xs text-slate-400">{sub}</span>}
      </div>
      <p className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
        {value}
      </p>
      <p className="text-xs text-slate-500 mt-1">{label}</p>
    </div>
  );
};

const SectionTitle = ({ children, icon: Icon }) => (
  <div className="flex items-center gap-2 mb-4">
    {Icon && <Icon className="w-4 h-4 text-slate-500" />}
    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">{children}</h3>
  </div>
);

const EmptyState = ({ message = 'Henuz veri yok' }) => (
  <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl p-10 text-center">
    <p className="text-sm text-slate-500">{message}</p>
  </div>
);

const StatisticsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({
    overview: null, users: null, regional: null, wg: null,
    proposals: null, votes: null, views: null, surveysEvents: null,
  });
  const [tab, setTab] = useState('overview');

  // Tarih filtresi state
  const [dateFilter, setDateFilter] = useState({ preset: 'all', start: '', end: '' });
  // En aktif kullanicilar sayfalama state
  const [activeUsers, setActiveUsers] = useState({ items: [], page: 1, total: 0, total_pages: 0, loading: false });

  const isAllowed = user && ALLOWED_ROLES.includes(user.role);

  // Preset -> start/end donusum
  const getFilterParams = useCallback(() => {
    const params = {};
    if (dateFilter.preset === 'custom') {
      if (dateFilter.start) params.start_date = dateFilter.start;
      if (dateFilter.end) params.end_date = dateFilter.end;
    } else if (dateFilter.preset !== 'all') {
      const days = { '7d': 7, '30d': 30, '90d': 90, '365d': 365 }[dateFilter.preset] || 0;
      if (days > 0) {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - days);
        params.start_date = start.toISOString().slice(0, 10);
        params.end_date = end.toISOString().slice(0, 10);
      }
    }
    return params;
  }, [dateFilter]);

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const params = getFilterParams();
      const [ov, us, rg, wg, pr, vt, cv, se] = await Promise.all([
        statisticsAPI.overview(params),
        statisticsAPI.users(params),
        statisticsAPI.regional(params),
        statisticsAPI.workingGroups(params),
        statisticsAPI.proposals(params),
        statisticsAPI.votes(params),
        statisticsAPI.contentViews(params),
        statisticsAPI.surveysEvents(params),
      ]);
      setData({
        overview: ov.data, users: us.data, regional: rg.data, wg: wg.data,
        proposals: pr.data, votes: vt.data, views: cv.data, surveysEvents: se.data,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Veriler yuklenirken hata olustu');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getFilterParams]);

  // En aktif kullanicilar (paginated) yukle
  const fetchActiveUsers = useCallback(async (page = 1) => {
    setActiveUsers(prev => ({ ...prev, loading: true }));
    try {
      const params = { ...getFilterParams(), page, page_size: 100 };
      const r = await statisticsAPI.activeUsers(params);
      setActiveUsers({
        items: r.data.items || [],
        page: r.data.page,
        total: r.data.total,
        total_pages: r.data.total_pages,
        loading: false,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kullanicilar yuklenemedi');
      setActiveUsers(prev => ({ ...prev, loading: false }));
    }
  }, [getFilterParams]);

  useEffect(() => {
    if (isAllowed) fetchAll();
    else setLoading(false);
  }, [isAllowed, fetchAll]);

  // Kullanicilar sekmesi acildiginda 1. sayfayi yukle
  useEffect(() => {
    if (isAllowed && tab === 'users') fetchActiveUsers(1);
  }, [isAllowed, tab, fetchActiveUsers]);

  const handleExportExcel = async () => {
    try {
      toast.info('Excel dosyasi hazirlaniyor...');
      const resp = await axios.get(statisticsAPI.exportExcelUrl(getFilterParams()), {
        withCredentials: true,
        responseType: 'blob',
        headers: { 'X-CSRF-Token': document.cookie.split('csrf_token=')[1]?.split(';')[0] || '' },
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.setAttribute('download', `istatistikler_${ts}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel indirildi');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Excel olusturulamadi');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (!isAllowed) {
    return (
      <div className="max-w-3xl mx-auto mt-20 px-6">
        <Card>
          <CardContent className="p-10 text-center">
            <div className="w-14 h-14 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Erisim Yetkiniz Yok</h2>
            <p className="text-sm text-slate-500">
              Istatistikler modulune yalniz Sistem Yoneticisi, Kent Konseyi Baskani,
              Genel Sekreter ve Genel Sekreter Yardimcisi erisebilir.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const ov = data.overview?.users || {};
  const cov = data.overview?.content || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6 print:p-0" data-testid="statistics-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <BarChart3 className="w-6 h-6 text-blue-600" />
            Istatistikler
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Kullanici katilimi, etkilesim ve karar sureclerinin kapsamli analizi
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={refreshing} data-testid="stats-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> Yenile
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="stats-print-btn">
            <Printer className="w-4 h-4 mr-2" /> Yazdir
          </Button>
          <Button size="sm" onClick={handleExportExcel} className="bg-emerald-600 hover:bg-emerald-700" data-testid="stats-excel-btn">
            <FileDown className="w-4 h-4 mr-2" /> Excel Indir
          </Button>
        </div>
      </div>

      {/* === TARIH FILTRESI === */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-3 print:hidden" data-testid="date-filter-panel">
        <div className="flex items-center gap-2 text-sm text-slate-600 font-medium">
          <Calendar className="w-4 h-4 text-blue-600" /> Tarih Araligi:
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { v: 'all', l: 'Tumu' },
            { v: '7d', l: 'Son 7 gun' },
            { v: '30d', l: 'Son 30 gun' },
            { v: '90d', l: 'Son 90 gun' },
            { v: '365d', l: 'Son 1 yil' },
            { v: 'custom', l: 'Ozel' },
          ].map(opt => (
            <button
              key={opt.v}
              onClick={() => setDateFilter(prev => ({ ...prev, preset: opt.v }))}
              data-testid={`filter-preset-${opt.v}`}
              className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                dateFilter.preset === opt.v
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
        {dateFilter.preset === 'custom' && (
          <div className="flex items-center gap-2 ml-auto">
            <input
              type="date"
              value={dateFilter.start}
              onChange={e => setDateFilter(prev => ({ ...prev, start: e.target.value }))}
              data-testid="filter-start-date"
              className="text-xs border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
            <span className="text-slate-400 text-xs">→</span>
            <input
              type="date"
              value={dateFilter.end}
              onChange={e => setDateFilter(prev => ({ ...prev, end: e.target.value }))}
              data-testid="filter-end-date"
              className="text-xs border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
        )}
        <Button
          size="sm"
          onClick={fetchAll}
          disabled={refreshing}
          data-testid="filter-apply-btn"
          className="ml-auto bg-blue-600 hover:bg-blue-700 h-8"
        >
          Uygula
        </Button>
      </div>

      {/* Yazdirilabilir baslik */}
      <div className="hidden print:block mb-6 border-b border-slate-300 pb-4">
        <h1 className="text-xl font-bold">Kent Konseyi Dijital Katilim Platformu - Istatistik Raporu</h1>
        <p className="text-sm text-slate-600 mt-1">
          Rapor Tarihi: {new Date().toLocaleString('tr-TR')} |
          Rapor Hazirlayan: {user.name} {user.surname} ({ROLE_LABELS[user.role] || user.role})
        </p>
      </div>

      {/* Ust KPI satiri - her zaman gorunur */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Kpi icon={Circle} label="Su Anda Online" value={ov.online ?? 0} sub="son 60 dk" color="green" />
        <Kpi icon={Users} label="Toplam Kullanici" value={(ov.total ?? 0).toLocaleString('tr-TR')} color="blue" />
        <Kpi icon={Activity} label="Gunluk Aktif" value={(ov.dau ?? 0).toLocaleString('tr-TR')} sub="DAU" color="blue" />
        <Kpi icon={Activity} label="Aylik Aktif" value={(ov.mau ?? 0).toLocaleString('tr-TR')} sub="MAU" color="purple" />
        <Kpi icon={TrendingUp} label="Katilim Orani" value={`%${ov.participation_rate ?? 0}`} sub="30 gun / toplam" color="amber" />
        <Kpi icon={TrendingUp} label="Yillik Buyume" value={`%${ov.yoy_growth ?? 0}`} sub="YoY" color={(ov.yoy_growth ?? 0) >= 0 ? 'green' : 'red'} />
      </div>

      {/* Sekmeler */}
      <Tabs value={tab} onValueChange={setTab} className="space-y-5 print:space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100/70 p-1 rounded-xl print:hidden">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-overview"><BarChart3 className="w-3.5 h-3.5 mr-1.5" />Ozet</TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-users"><Users className="w-3.5 h-3.5 mr-1.5" />Kullanicilar</TabsTrigger>
          <TabsTrigger value="regional" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-regional"><MapPin className="w-3.5 h-3.5 mr-1.5" />Bolgesel</TabsTrigger>
          <TabsTrigger value="wg" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-wg"><UsersRound className="w-3.5 h-3.5 mr-1.5" />Calisma Gruplari</TabsTrigger>
          <TabsTrigger value="proposals" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-proposals"><Lightbulb className="w-3.5 h-3.5 mr-1.5" />Oneri & Karar</TabsTrigger>
          <TabsTrigger value="votes" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-votes"><Vote className="w-3.5 h-3.5 mr-1.5" />Oylama</TabsTrigger>
          <TabsTrigger value="views" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-views"><Eye className="w-3.5 h-3.5 mr-1.5" />Goruntulenme</TabsTrigger>
          <TabsTrigger value="surveys" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-surveys"><ClipboardList className="w-3.5 h-3.5 mr-1.5" />Anket/Etkinlik</TabsTrigger>
        </TabsList>

        {/* OZET */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card>
              <CardHeader><CardTitle className="text-sm">Icerik Sayilari</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <Kpi icon={Megaphone} label="Duyurular" value={cov.announcements ?? 0} color="blue" />
                  <Kpi icon={Calendar} label="Toplantilar" value={cov.meetings ?? 0} color="amber" />
                  <Kpi icon={ClipboardList} label="Anketler" value={cov.surveys ?? 0} color="purple" />
                  <Kpi icon={Calendar} label="Etkinlikler" value={cov.events ?? 0} color="green" />
                  <Kpi icon={Lightbulb} label="Tartismalar" value={cov.discussions ?? 0} color="amber" />
                  <Kpi icon={Vote} label="Oylamalar" value={cov.votes ?? 0} color="red" />
                  <Kpi icon={Activity} label="Kararlar" value={cov.decisions ?? 0} color="purple" />
                  <Kpi icon={UsersRound} label="Aktif Organ" value={cov.active_organs ?? 0} color="blue" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-sm">Kullanici Aktivitesi Ozeti</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={[
                    { label: 'Gunluk', value: ov.dau ?? 0 },
                    { label: 'Haftalik', value: ov.wau ?? 0 },
                    { label: 'Aylik', value: ov.mau ?? 0 },
                    { label: 'Yillik', value: ov.yau ?? 0 },
                    { label: 'Toplam', value: ov.total ?? 0 },
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#2563EB" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* KULLANICILAR */}
        <TabsContent value="users" className="space-y-6">
          {data.users && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <Kpi icon={Users} label="Pasif Kullanici (90+ gun)" value={data.users.passive_user_count} color="amber" />
                <Kpi icon={Users} label="Hic Giris Yapmamis" value={data.users.never_logged_in_count} color="red" />
                <Kpi icon={TrendingUp} label="Bu Yil Yeni Kayit" value={ov.this_year_new ?? 0} color="green" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <Card>
                  <CardHeader><CardTitle className="text-sm">Rol Dagilimi</CardTitle></CardHeader>
                  <CardContent>
                    {data.users.role_distribution.length === 0 ? <EmptyState /> : (
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie data={data.users.role_distribution.map(r => ({ ...r, label: ROLE_LABELS[r.role] || r.role }))}
                            dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={90} label>
                            {data.users.role_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="text-sm">Yillara Gore Kayit</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={data.users.yearly_registrations}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Line type="monotone" dataKey="count" stroke="#2563EB" strokeWidth={2} dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <Card>
                  <CardHeader><CardTitle className="text-sm">Cinsiyet</CardTitle></CardHeader>
                  <CardContent>
                    {data.users.gender_distribution.length === 0 ? <EmptyState /> : (
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie data={data.users.gender_distribution} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={70} label>
                            {data.users.gender_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-sm">Egitim Durumu</CardTitle></CardHeader>
                  <CardContent>
                    {data.users.education_distribution.length === 0 ? <EmptyState /> : (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={data.users.education_distribution} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis dataKey="label" type="category" tick={{ fontSize: 10 }} width={90} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#8B5CF6" radius={[0, 6, 6, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-sm">Meslek (Top 15)</CardTitle></CardHeader>
                  <CardContent>
                    {data.users.profession_top.length === 0 ? <EmptyState /> : (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={data.users.profession_top} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis dataKey="label" type="category" tick={{ fontSize: 10 }} width={100} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#10B981" radius={[0, 6, 6, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-sm">En Aktif Kullanicilar (Giris Sayisi) - Top 1000</CardTitle>
                  <div className="text-xs text-slate-500" data-testid="active-users-meta">
                    Toplam: <span className="font-semibold text-slate-700">{activeUsers.total}</span>
                    {activeUsers.total_pages > 0 && <> | Sayfa {activeUsers.page} / {activeUsers.total_pages}</>}
                  </div>
                </CardHeader>
                <CardContent>
                  {activeUsers.loading ? (
                    <div className="flex justify-center py-10">
                      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : activeUsers.items.length === 0 ? <EmptyState /> : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="active-users-table">
                          <thead>
                            <tr className="border-b border-slate-200 text-left text-slate-500 text-xs uppercase tracking-wider">
                              <th className="py-2 pr-3 w-12">#</th>
                              <th className="py-2 pr-3">Ad Soyad</th>
                              <th className="py-2 pr-3">E-posta</th>
                              <th className="py-2 pr-3">Rol</th>
                              <th className="py-2 pr-3 text-right">Giris Sayisi</th>
                              <th className="py-2 pr-3">Son Giris</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeUsers.items.map((u, i) => {
                              const globalRank = (activeUsers.page - 1) * 100 + i + 1;
                              return (
                                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                                  <td className="py-2 pr-3 text-slate-400 font-mono text-xs">{globalRank}</td>
                                  <td className="py-2 pr-3 font-medium text-slate-900">{u.name}</td>
                                  <td className="py-2 pr-3 text-slate-600">{u.email}</td>
                                  <td className="py-2 pr-3"><Badge variant="outline" className="text-xs">{ROLE_LABELS[u.role] || u.role}</Badge></td>
                                  <td className="py-2 pr-3 text-right font-semibold">{u.login_count}</td>
                                  <td className="py-2 pr-3 text-slate-500">{u.last_login_at ? new Date(u.last_login_at).toLocaleString('tr-TR') : '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {/* Sayfalama kontrolleri */}
                      {activeUsers.total_pages > 1 && (
                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100 print:hidden">
                          <div className="text-xs text-slate-500">
                            Sayfa basina 100 kullanici
                          </div>
                          <div className="flex items-center gap-1" data-testid="active-users-pagination">
                            <Button
                              size="sm" variant="outline"
                              disabled={activeUsers.page <= 1 || activeUsers.loading}
                              onClick={() => fetchActiveUsers(1)}
                              data-testid="pagination-first"
                              className="h-8 px-2"
                            >
                              {'«'}
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              disabled={activeUsers.page <= 1 || activeUsers.loading}
                              onClick={() => fetchActiveUsers(activeUsers.page - 1)}
                              data-testid="pagination-prev"
                              className="h-8 px-2"
                            >
                              {'‹ Onceki'}
                            </Button>
                            {/* Sayfa numaralari (en fazla 5) */}
                            {Array.from({ length: Math.min(5, activeUsers.total_pages) }, (_, idx) => {
                              let p;
                              const t = activeUsers.total_pages;
                              const c = activeUsers.page;
                              if (t <= 5) p = idx + 1;
                              else if (c <= 3) p = idx + 1;
                              else if (c >= t - 2) p = t - 4 + idx;
                              else p = c - 2 + idx;
                              return (
                                <Button
                                  key={p}
                                  size="sm"
                                  variant={p === activeUsers.page ? 'default' : 'outline'}
                                  onClick={() => fetchActiveUsers(p)}
                                  disabled={activeUsers.loading}
                                  data-testid={`pagination-page-${p}`}
                                  className={`h-8 w-8 p-0 ${p === activeUsers.page ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                                >
                                  {p}
                                </Button>
                              );
                            })}
                            <Button
                              size="sm" variant="outline"
                              disabled={activeUsers.page >= activeUsers.total_pages || activeUsers.loading}
                              onClick={() => fetchActiveUsers(activeUsers.page + 1)}
                              data-testid="pagination-next"
                              className="h-8 px-2"
                            >
                              {'Sonraki ›'}
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              disabled={activeUsers.page >= activeUsers.total_pages || activeUsers.loading}
                              onClick={() => fetchActiveUsers(activeUsers.total_pages)}
                              data-testid="pagination-last"
                              className="h-8 px-2"
                            >
                              {'»'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* BOLGESEL */}
        <TabsContent value="regional" className="space-y-6">
          {data.regional && (
            <>
              <Card>
                <CardHeader><CardTitle className="text-sm">Ilce Bazli Kullanici Dagilimi</CardTitle></CardHeader>
                <CardContent>
                  {data.regional.by_ilce.length === 0 ? <EmptyState /> : (
                    <ResponsiveContainer width="100%" height={Math.max(260, data.regional.by_ilce.length * 28)}>
                      <BarChart data={data.regional.by_ilce} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis dataKey="ilce_name" type="category" tick={{ fontSize: 11 }} width={130} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#2563EB" radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-sm">Mahalle Bazli (Top 20)</CardTitle></CardHeader>
                <CardContent>
                  {data.regional.by_mahalle_top20.length === 0 ? <EmptyState /> : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 text-left text-slate-500 text-xs uppercase">
                            <th className="py-2 pr-3">Mahalle</th>
                            <th className="py-2 pr-3">Ilce</th>
                            <th className="py-2 pr-3 text-right">Kullanici</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.regional.by_mahalle_top20.map((m, i) => (
                            <tr key={m.mahalle_id || i} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 pr-3 font-medium">{m.mahalle_name}</td>
                              <td className="py-2 pr-3 text-slate-600">{m.ilce_name}</td>
                              <td className="py-2 pr-3 text-right font-semibold">{m.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* CALISMA GRUPLARI */}
        <TabsContent value="wg" className="space-y-6">
          {data.wg && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi icon={UsersRound} label="Toplam CG" value={data.wg.summary.total_working_groups} color="blue" />
                <Kpi icon={Users} label="Toplam Uye" value={data.wg.summary.total_members} color="blue" />
                <Kpi icon={Activity} label="Aktif Uye (30g)" value={data.wg.summary.active_members_30d} color="green" />
                <Kpi icon={Lightbulb} label="Toplam Tartisma" value={data.wg.summary.total_discussions} color="amber" />
              </div>
              <Card>
                <CardHeader><CardTitle className="text-sm">En Aktif 50 Calisma Grubu</CardTitle></CardHeader>
                <CardContent>
                  {data.wg.top_groups.length === 0 ? <EmptyState /> : (
                    <div className="overflow-auto max-h-[600px] border border-slate-100 rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-white z-10 shadow-sm">
                          <tr className="border-b border-slate-200 text-left text-slate-500 text-xs uppercase">
                            <th className="py-2 px-3">#</th>
                            <th className="py-2 pr-3">Grup</th>
                            <th className="py-2 pr-3">Bagli Meclis</th>
                            <th className="py-2 pr-3 text-right">Uye</th>
                            <th className="py-2 pr-3 text-right">Tartisma</th>
                            <th className="py-2 pr-3 text-right">Yorum</th>
                            <th className="py-2 pr-3 text-right">Oylama</th>
                            <th className="py-2 pr-3 text-right">Karar</th>
                            <th className="py-2 pr-3 text-right">Skor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.wg.top_groups.map((g, idx) => (
                            <tr key={g.id} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 px-3 text-slate-400 font-mono text-xs">{idx + 1}</td>
                              <td className="py-2 pr-3 font-medium">{g.name}</td>
                              <td className="py-2 pr-3 text-slate-600 text-xs">{g.meclis_name || '—'}</td>
                              <td className="py-2 pr-3 text-right">{g.members}</td>
                              <td className="py-2 pr-3 text-right">{g.discussions}</td>
                              <td className="py-2 pr-3 text-right">{g.comments}</td>
                              <td className="py-2 pr-3 text-right">{g.votes}</td>
                              <td className="py-2 pr-3 text-right">{g.decisions}</td>
                              <td className="py-2 pr-3 text-right font-semibold text-blue-600">{g.score}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ONERI & KARAR */}
        <TabsContent value="proposals" className="space-y-6">
          {data.proposals && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi icon={Lightbulb} label="Toplam Tartisma" value={data.proposals.total_discussions} color="amber" />
                <Kpi icon={Activity} label="Toplam Yorum" value={data.proposals.total_comments} color="blue" />
                <Kpi icon={Vote} label="Karar Sayisi" value={(data.proposals.outcome_distribution||[]).reduce((a, b) => a + b.count, 0)} color="green" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <Card>
                  <CardHeader><CardTitle className="text-sm">Karar Sonuclari (Kabul/Red/Ertelendi)</CardTitle></CardHeader>
                  <CardContent>
                    {data.proposals.outcome_distribution.length === 0 ? <EmptyState /> : (
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie data={data.proposals.outcome_distribution} dataKey="count" nameKey="outcome" cx="50%" cy="50%" outerRadius={80} label>
                            {data.proposals.outcome_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip /><Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-sm">Aylik Tartisma Aciliskari (12 Ay)</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={data.proposals.monthly_discussions}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Line type="monotone" dataKey="count" stroke="#F59E0B" strokeWidth={2} dot />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
              <Card>
                <CardHeader><CardTitle className="text-sm">En Etkili Oneriler (Yorum Sayisina Gore)</CardTitle></CardHeader>
                <CardContent>
                  {data.proposals.top_discussions.length === 0 ? <EmptyState /> : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 text-left text-slate-500 text-xs uppercase">
                            <th className="py-2 pr-3">Baslik</th>
                            <th className="py-2 pr-3 text-right">Yorum</th>
                            <th className="py-2 pr-3">Tarih</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.proposals.top_discussions.map(d => (
                            <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 pr-3 font-medium">{d.title}</td>
                              <td className="py-2 pr-3 text-right font-semibold">{d.comment_count}</td>
                              <td className="py-2 pr-3 text-slate-500">{d.created_at ? new Date(d.created_at).toLocaleDateString('tr-TR') : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* OYLAMA */}
        <TabsContent value="votes" className="space-y-6">
          {data.votes && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi icon={Vote} label="Toplam Oylama" value={data.votes.total_votes} color="blue" />
                <Kpi icon={Circle} label="Aktif" value={data.votes.active_votes} color="green" />
                <Kpi icon={Activity} label="Kapanan" value={data.votes.closed_votes} color="slate" />
                <Kpi icon={TrendingUp} label="Ortalama Katilim" value={data.votes.avg_participation} color="purple" />
              </div>
              <Card>
                <CardHeader><CardTitle className="text-sm">Aylik Oy Kayitlari (12 Ay)</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={data.votes.monthly_vote_records}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2} dot />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-sm">En Cok Katilim Alan Oylamalar</CardTitle></CardHeader>
                <CardContent>
                  {data.votes.top_votes.length === 0 ? <EmptyState /> : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 text-left text-slate-500 text-xs uppercase">
                            <th className="py-2 pr-3">Baslik</th>
                            <th className="py-2 pr-3 text-right">Toplam Oy</th>
                            <th className="py-2 pr-3">Durum</th>
                            <th className="py-2 pr-3">Tarih</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.votes.top_votes.map(v => (
                            <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 pr-3 font-medium">{v.title}</td>
                              <td className="py-2 pr-3 text-right font-semibold">{v.total_votes}</td>
                              <td className="py-2 pr-3"><Badge variant="outline" className="text-xs">{v.status}</Badge></td>
                              <td className="py-2 pr-3 text-slate-500">{v.created_at ? new Date(v.created_at).toLocaleDateString('tr-TR') : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* GORUNTULENME */}
        <TabsContent value="views" className="space-y-6">
          {data.views && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(data.views.summary).map(([ct, v]) => {
                  const label = ({ announcement: 'Duyuru', meeting: 'Toplanti', survey: 'Anket', event: 'Etkinlik' })[ct];
                  return (
                    <Card key={ct}>
                      <CardContent className="p-5">
                        <p className="text-xs text-slate-500 mb-1">{label}</p>
                        <p className="text-xl font-semibold text-slate-900">{v.unique_viewers} <span className="text-sm text-slate-400 font-normal">kisi gordu</span></p>
                        <p className="text-xs text-slate-400 mt-1">Toplam izleme: {v.total_views}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {[
                  { key: 'top_announcements', title: 'En Cok Goruntulenen Duyurular' },
                  { key: 'top_meetings', title: 'En Cok Goruntulenen Toplantilar' },
                  { key: 'top_surveys', title: 'En Cok Goruntulenen Anketler' },
                  { key: 'top_events', title: 'En Cok Goruntulenen Etkinlikler' },
                ].map(({ key, title }) => (
                  <Card key={key}>
                    <CardHeader><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
                    <CardContent>
                      {(data.views[key] || []).length === 0 ? <EmptyState /> : (
                        <ul className="space-y-2">
                          {data.views[key].map(item => (
                            <li key={item.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                              <span className="truncate pr-3">{item.title}</span>
                              <Badge className="bg-blue-100 text-blue-700 border-0">{item.views}</Badge>
                            </li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* ANKET & ETKINLIK */}
        <TabsContent value="surveys" className="space-y-6">
          {data.surveysEvents && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi icon={ClipboardList} label="Toplam Anket" value={data.surveysEvents.surveys.total} color="purple" />
                <Kpi icon={Activity} label="Aktif Anket" value={data.surveysEvents.surveys.active} color="green" />
                <Kpi icon={Users} label="Anket Yaniti" value={data.surveysEvents.surveys.total_responses} color="blue" />
                <Kpi icon={TrendingUp} label="Anket basina Ort. Yanit" value={data.surveysEvents.surveys.avg_responses_per_survey} color="amber" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi icon={Calendar} label="Toplam Etkinlik" value={data.surveysEvents.events.total} color="green" />
                <Kpi icon={Users} label="Toplam Basvuru" value={data.surveysEvents.events.total_applications} color="blue" />
                <Kpi icon={Activity} label="Onaylanan Basvuru" value={data.surveysEvents.events.approved_applications} color="green" />
                <Kpi icon={Megaphone} label="Toplam Duyuru" value={data.surveysEvents.announcements.total} color="amber" />
              </div>
              <Card>
                <CardHeader><CardTitle className="text-sm">Meclis Bazli Toplanti Sayilari</CardTitle></CardHeader>
                <CardContent>
                  {data.surveysEvents.meetings.by_meclis.length === 0 ? <EmptyState /> : (
                    <ResponsiveContainer width="100%" height={Math.max(280, data.surveysEvents.meetings.by_meclis.length * 32)}>
                      <BarChart data={data.surveysEvents.meetings.by_meclis} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis dataKey="meclis_name" type="category" tick={{ fontSize: 11 }} width={140} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#F59E0B" radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Yazdirma CSS */}
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 12mm; }
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
          [role="tab"] { display: none !important; }
          [role="tabpanel"] { display: block !important; page-break-inside: avoid; margin-bottom: 16px; }
        }
      `}</style>
    </div>
  );
};

export default StatisticsPage;
